/**
 * "Change format…" (ADR 0036): turn the open graphic into another of PRISM's formats (or a custom size), e.g. a
 * 16:9 graphic duplicated in PRISM into vertical 9:16. The content is scaled and re-placed by its anchors
 * (model/reformat.ts) with a live preview; applying it is one undo step, and the Problems panel then lists what
 * needs a touch-up. Opened from the top bar's format select and the inspector's Canvas section.
 *
 * Layouts (ADR 0037): the same dialog adds a layout ("+" on the layout tabs) or changes one that is not the primary;
 * both adapt the primary layout to the size and keep what differs as the layout's overrides. The frame rate is the
 * graphic's then, and the shapes the graphic has a layout for are disabled.
 */
import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { renderStatic } from '@prism-graphics/runtime/static';
import { CUSTOM_SIZE_LIMITS, fps, layoutsOf, rateFor, ratesFor, sceneForLayout, type FrameRate } from '@prism-graphics/scene-schema';
import { t } from './i18n.ts';
import { modelGeo } from './model/commands.ts';
import { aspectTaken, primaryOf } from './model/layouts.ts';
import { adaptCanvas, contentScaleOf, defaultContent } from './model/reformat.ts';
import { safeArea } from './model/snapping.ts';
import { Modal } from './panels/Dialogs.tsx';
import { assetCache } from './state/assets.ts';
import { activeLayout, sceneView, useEditor, type EditorStore } from './state/store.ts';
import { NumberField, Row } from './ui/controls.tsx';
import { FormatOptions, RateOptions, isPreset, parseRate, parseSize, rateValue, shapeLabel, sizeKey } from './ui/formats.tsx';

const PREVIEW_BOX = 220;

export function ChangeFormatDialog({ store }: { store: EditorStore }) {
  const initial = useEditor(store, (s) => s.ui.formatDialog);
  if (!initial) return null;
  return <FormatDialog store={store} initial={initial} onClose={() => store.setUi({ formatDialog: null, formatDialogMode: 'change' })} />;
}

function FormatDialog({ store, initial, onClose }: { store: EditorStore; initial: { width: number; height: number }; onClose: () => void }) {
  const view = useEditor(store, sceneView);
  const doc = useEditor(store, (s) => s.history.present);
  const layout = useEditor(store, activeLayout);
  const adding = useEditor(store, (s) => s.ui.formatDialogMode === 'add');
  const testData = useEditor(store, (s) => s.testData);
  const graphicsSafe = useEditor(store, (s) => s.ui.graphicsSafe);
  // A layout (added, or changed when it is not the primary) is adapted from the primary layout.
  const primary = primaryOf(doc);
  const ofLayout = adding || layout !== primary;
  const scene = useMemo(() => (ofLayout ? sceneForLayout(doc) : view), [ofLayout, doc, view]);
  const label = layoutsOf(doc).find((l) => l.id === layout)?.label ?? layout;
  const taken = (size: { width: number; height: number }) => aspectTaken(doc, size, adding ? undefined : layout);
  const from = { width: scene.canvas.width, height: scene.canvas.height };
  const [custom, setCustom] = useState(!isPreset(initial));
  const [size, setSize] = useState(initial);
  const [rate, setRate] = useState<FrameRate>(() => rateFor(initial, scene.canvas.frameRate));
  const [keep, setKeep] = useState(false);
  const fitScale = (to: { width: number; height: number }) => Math.round(contentScaleOf(from, to, { content: defaultContent(from, to) }) * 10000) / 100;
  const [percent, setPercent] = useState(() => fitScale(initial));

  const valid =
    size.width >= CUSTOM_SIZE_LIMITS.minWidth && size.width <= CUSTOM_SIZE_LIMITS.maxWidth && size.height >= CUSTOM_SIZE_LIMITS.minHeight && size.height <= CUSTOM_SIZE_LIMITS.maxHeight && !taken(size);
  // The primary's geometry, for placing unanchored elements (measured off-screen when another layout is on the canvas).
  const geo = useMemo(() => (ofLayout && layout !== primary ? (store.measure?.(scene) ?? modelGeo(scene)) : store.geo), [ofLayout, layout, primary, scene, store]);
  const choose = (next: { width: number; height: number }) => {
    setSize(next);
    setRate(rateFor(next, rate));
    setPercent(fitScale(next));
  };
  const adapted = useMemo(
    () => (valid ? adaptCanvas(scene, { ...size, frameRate: ofLayout ? scene.canvas.frameRate : rate }, { content: 'reposition', contentScale: keep ? 1 : percent / 100, sizeOf: (id) => geo.size?.(id) ?? geo.bounds(id) }) : undefined),
    [scene, size, rate, keep, percent, valid, geo, ofLayout],
  );

  const rates = ratesFor(size);
  const unchanged = !ofLayout && size.width === from.width && size.height === from.height && rate.num * scene.canvas.frameRate.den === scene.canvas.frameRate.num * rate.den;
  const apply = () => {
    if (!adapted || unchanged) return;
    if (adding) store.addLayout(adapted);
    else if (ofLayout) store.replaceLayout(layout, adapted);
    else store.apply(() => adapted);
    onClose();
  };
  const title = adding ? t('format.add.title') : ofLayout ? t('format.layout.title', { label }) : t('format.change.title');

  return (
    <Modal title={title} onClose={onClose} testId="change-format-dialog" labelledBy="change-format-title">
      <p className="hint">
        {adding
          ? t('format.add.from', { width: from.width, height: from.height, shape: shapeLabel(from) })
          : ofLayout
            ? t('format.layout.from', { label, width: from.width, height: from.height })
            : t('format.change.from', { width: from.width, height: from.height, fps: Math.round(fps(scene.canvas.frameRate) * 100) / 100, shape: shapeLabel(from) })}
      </p>
      <div className="format-dialog">
        <div className="format-fields">
          <Row className="two">
            <label className="field select">
              <span className="field-label">{t('format.change.target')}</span>
              <select
                value={custom ? 'custom-new' : sizeKey(size)}
                data-testid="cf-format"
                onChange={(e) => {
                  const v = e.target.value;
                  setCustom(v === 'custom-new');
                  if (v !== 'custom-new') choose(parseSize(v));
                }}
              >
                <FormatOptions extra={[{ value: 'custom-new', label: t('format.change.customNew') }]} disabled={taken} />
              </select>
            </label>
            {!ofLayout && (
              <label className="field select">
                <span className="field-label">{t('top.frameRate')}</span>
                <select value={rateValue(rate, rates)} data-testid="cf-rate" onChange={(e) => setRate(parseRate(e.target.value))}>
                  <RateOptions rates={rates} current={rate} />
                </select>
              </label>
            )}
          </Row>
          {custom && (
            <Row className="two">
              <NumberField label={t('inspector.width')} short="W" value={size.width} min={CUSTOM_SIZE_LIMITS.minWidth} max={CUSTOM_SIZE_LIMITS.maxWidth} testId="cf-width" onChange={(w) => choose({ ...size, width: Math.round(w) })} />
              <NumberField label={t('inspector.height')} short="H" value={size.height} min={CUSTOM_SIZE_LIMITS.minHeight} max={CUSTOM_SIZE_LIMITS.maxHeight} testId="cf-height" onChange={(h) => choose({ ...size, height: Math.round(h) })} />
            </Row>
          )}
          <p className="hint" data-testid="cf-shape">
            {shapeLabel(size)}
          </p>
          {taken(size) && (
            <p className="hint warn" data-testid="cf-taken">
              {t('format.taken')}
            </p>
          )}
          <fieldset className="format-content">
            <legend className="field-label">{t('format.change.content')}</legend>
            <label className="toggle">
              <input type="radio" name="cf-content" checked={!keep} onChange={() => setKeep(false)} data-testid="cf-fit" />
              <span>{t('format.change.fit')}</span>
            </label>
            {!keep && (
              <label className="field range">
                <span className="field-label">{t('format.change.size', { percent })}</span>
                <input type="range" min={10} max={400} step={0.25} value={percent} onChange={(e) => setPercent(Number(e.target.value))} data-testid="cf-scale" aria-valuetext={t('format.change.size', { percent })} />
              </label>
            )}
            <label className="toggle">
              <input type="radio" name="cf-content" checked={keep} onChange={() => setKeep(true)} data-testid="cf-keep" />
              <span>{t('format.change.keep')}</span>
            </label>
          </fieldset>
          <p className="hint">{t('format.change.hint')}</p>
        </div>
        {adapted && <FormatPreview scene={adapted} testData={testData} safeMargin={graphicsSafe} />}
      </div>
      <div className="modal-actions">
        <button type="button" className="btn" onClick={onClose} data-testid="cf-cancel">
          {t('format.change.cancel')}
        </button>
        <button type="button" className="btn primary" onClick={apply} disabled={!adapted || unchanged} data-testid="cf-apply">
          {adding ? t('format.add.apply') : t('format.change.apply')}
        </button>
      </div>
    </Modal>
  );
}

/** The adapted graphic at the step-1 hold, scaled into a small box, with the graphics-safe area. */
function FormatPreview({ scene, testData, safeMargin }: { scene: ReturnType<typeof adaptCanvas>; testData: Record<string, unknown>; safeMargin: number }) {
  const hostRef = useRef<HTMLDivElement>(null);
  const { width: W, height: H } = scene.canvas;
  const k = PREVIEW_BOX / Math.max(W, H);
  useLayoutEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    const shadow = host.shadowRoot ?? host.attachShadow({ mode: 'open' });
    try {
      renderStatic(shadow, scene, { families: assetCache.families, imageUrl: assetCache.imageUrl, state: 'hold', data: testData });
    } catch {
      // A scene the runtime cannot show yet (e.g. an extreme custom size): the preview stays as it was.
    }
  }, [scene, testData]);
  const safe = safeArea(W, H, safeMargin);
  return (
    <figure className="format-preview" data-testid="cf-preview" data-width={W} data-height={H}>
      <div className="format-preview-stage checker" style={{ width: W * k, height: H * k }}>
        <div ref={hostRef} className="format-preview-host" style={{ width: W, height: H, transform: `scale(${k})` }} />
        <div className="format-preview-safe" style={{ left: safe.x * k, top: safe.y * k, width: safe.width * k, height: safe.height * k }} aria-hidden />
      </div>
      <figcaption className="hint">{t('format.change.preview', { width: W, height: H })}</figcaption>
    </figure>
  );
}
